import { ReactNode } from "react";

import { Step, isDefaultCommandStep } from "~/lib/pipeline";
import Duration, { DateLike } from "~/components/shared/Duration";
import { CommandJob } from "~/components/build/Show/lib/types/CommandJob";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardPortal,
} from "~/components/shared/HoverCard";
import { useSharedHovercard } from "~/components/build/Show/routes/list/-components/useSharedHovercard";

type TimingBreakdownVariant = "waiting" | "running";

function TimingBreakdownRow({
  duration,
  text,
  variant,
}: {
  duration: ReactNode;
  text: string;
  variant: TimingBreakdownVariant;
}) {
  const circleColor: Record<TimingBreakdownVariant, string> = {
    waiting: "var(--gray-500)",
    running: "var(--green-500)",
  };

  return (
    <div className="grid grid-cols-[10px_auto_1fr] items-center gap-2 whitespace-nowrap">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
      >
        <circle cx="5" cy="5" r="4" stroke={circleColor[variant]} strokeWidth="1.5" />
      </svg>
      <span className="tabular-nums">{duration}</span>
      <span className="text-start">{text}</span>
    </div>
  );
}

type TimingPhase = {
  from: DateLike;
  to?: DateLike | null;
  variant: TimingBreakdownVariant;
  text: string;
};

function TimingCell({
  runFrom,
  runTo,
  phases,
}: {
  runFrom: DateLike;
  runTo?: DateLike | null;
  phases: TimingPhase[];
}) {
  const showBreakdown = phases.length > 0;
  const { isOpen, openDelay, handleOpenChange } = useSharedHovercard({ defaultOpenDelay: 100 });

  const duration = <Duration.Short from={runFrom} to={runTo ?? undefined} />;

  if (!showBreakdown) {
    return (
      <span className="text-charcoal-700 min-w-[7ch] whitespace-nowrap text-right text-xs tabular-nums">
        {duration}
      </span>
    );
  }

  return (
    <HoverCard open={isOpen} openDelay={openDelay} closeDelay={50} onOpenChange={handleOpenChange}>
      <span className="text-charcoal-700 min-w-[7ch] whitespace-nowrap text-right text-xs tabular-nums">
        <HoverCardTrigger asChild={true}>
          <span>{duration}</span>
        </HoverCardTrigger>
      </span>
      <HoverCardPortal>
        <HoverCardContent
          size="max-lg"
          hideArrow={true}
          side="top"
          className="pointer-events-none z-50"
        >
          <div className="charcoal-800">
            {phases.map((phase, index) => (
              <TimingBreakdownRow
                key={index}
                duration={<Duration.Short from={phase.from} to={phase.to ?? undefined} />}
                text={phase.text}
                variant={phase.variant}
              />
            ))}
          </div>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}

const TIMING_PLACEHOLDER = (
  <span className="text-charcoal-700 min-w-[7ch] whitespace-nowrap text-right text-xs tabular-nums">
    --
  </span>
);

function getEndTime(job: CommandJob): string | null | undefined {
  return job.finishedAt ?? job.canceledAt ?? job.timedOutAt;
}

export function getJobTimingCell(job: CommandJob): ReactNode {
  const { runnableAt, startedAt } = job;
  const endTime = getEndTime(job);

  // Still waiting — live timer from runnableAt
  if (runnableAt && !startedAt && !endTime) {
    return (
      <TimingCell
        runFrom={runnableAt}
        phases={[{ from: runnableAt, variant: "waiting", text: "Waiting" }]}
      />
    );
  }

  // Canceled before starting — static duration from runnableAt to endTime
  if (runnableAt && !startedAt && endTime) {
    return (
      <TimingCell
        runFrom={runnableAt}
        runTo={endTime}
        phases={[{ from: runnableAt, to: endTime, variant: "waiting", text: "Waiting" }]}
      />
    );
  }

  // Running — live timer from startedAt
  if (startedAt && !endTime) {
    const phases: TimingPhase[] = [];

    if (runnableAt) {
      phases.push({ from: runnableAt, to: startedAt, variant: "waiting", text: "Waiting" });
    }
    phases.push({ from: startedAt, variant: "running", text: "Running" });

    return <TimingCell runFrom={startedAt} phases={phases} />;
  }

  // Finished — static duration
  if (startedAt && endTime) {
    const phases: TimingPhase[] = [];

    if (runnableAt) {
      phases.push({ from: runnableAt, to: startedAt, variant: "waiting", text: "Waiting" });
    }
    phases.push({ from: startedAt, to: endTime, variant: "running", text: "Running" });

    return <TimingCell runFrom={startedAt} runTo={endTime} phases={phases} />;
  }

  return TIMING_PLACEHOLDER;
}

export function getStepTimingCell(step: Step): ReactNode | undefined {
  if (!isDefaultCommandStep(step)) {
    return undefined;
  }

  const stats = step.statistics;
  if (!stats) {
    return TIMING_PLACEHOLDER;
  }

  const { latestJobRunnableAt, latestJobStartedAt, latestJobFinishedAt } = stats;

  // Still waiting
  if (latestJobRunnableAt && !latestJobStartedAt && !latestJobFinishedAt) {
    return (
      <TimingCell
        runFrom={latestJobRunnableAt}
        phases={[{ from: latestJobRunnableAt, variant: "waiting", text: "Waiting" }]}
      />
    );
  }

  // Canceled before starting — static duration
  if (latestJobRunnableAt && !latestJobStartedAt && latestJobFinishedAt) {
    return (
      <TimingCell
        runFrom={latestJobRunnableAt}
        runTo={latestJobFinishedAt}
        phases={[
          {
            from: latestJobRunnableAt,
            to: latestJobFinishedAt,
            variant: "waiting",
            text: "Waiting",
          },
        ]}
      />
    );
  }

  // Running
  if (latestJobStartedAt && !latestJobFinishedAt) {
    const phases: TimingPhase[] = [];

    if (latestJobRunnableAt) {
      phases.push({
        from: latestJobRunnableAt,
        to: latestJobStartedAt,
        variant: "waiting",
        text: "Waiting",
      });
    }
    phases.push({ from: latestJobStartedAt, variant: "running", text: "Running" });

    return <TimingCell runFrom={latestJobStartedAt} phases={phases} />;
  }

  // Finished
  if (latestJobStartedAt && latestJobFinishedAt) {
    const phases: TimingPhase[] = [];

    if (latestJobRunnableAt) {
      phases.push({
        from: latestJobRunnableAt,
        to: latestJobStartedAt,
        variant: "waiting",
        text: "Waiting",
      });
    }
    phases.push({
      from: latestJobStartedAt,
      to: latestJobFinishedAt,
      variant: "running",
      text: "Running",
    });

    return <TimingCell runFrom={latestJobStartedAt} runTo={latestJobFinishedAt} phases={phases} />;
  }

  return TIMING_PLACEHOLDER;
}
